import React from 'react';
import type { CtaComponent as CtaComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';

interface CtaComponentProps {
  component: CtaComponentType;
}

export const CtaComponent: React.FC<CtaComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Color priority: component.customColors > theme colors
  const bgColor =
    (data as any).customColors?.background || themeStyles.cta?.background || '#7C3AED';
  const textColor = (data as any).customColors?.text || themeStyles.cta?.text || '#ffffff';
  const buttonBgColor =
    (data as any).customColors?.buttonBackground || themeStyles.cta?.buttonBg || '#DC2626';
  const buttonTextColor =
    (data as any).customColors?.buttonText || themeStyles.cta?.buttonText || '#ffffff';

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  // Helper to convert position enum to CSS background-position
  const getBackgroundPosition = (position?: string): string => {
    if (!position) return 'center center';
    const positionMap: Record<string, string> = {
      center: 'center center',
      top: 'center top',
      bottom: 'center bottom',
      left: 'left center',
      right: 'right center',
      'top-left': 'left top',
      'top-right': 'right top',
      'bottom-left': 'left bottom',
      'bottom-right': 'right bottom',
    };
    return positionMap[position] || 'center center';
  };

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Check if background is a gradient
  const isGradient =
    bgColor?.startsWith('linear-gradient') || bgColor?.startsWith('radial-gradient');

  // Get background style - don't apply color if background image is enabled
  const getBackgroundStyle = () => {
    if (data.backgroundImage?.enabled) {
      return {};
    }
    return isGradient ? { backgroundImage: bgColor } : { backgroundColor: bgColor };
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" style={getBackgroundStyle()}>
      {/* Background Image */}
      {data.backgroundImage?.enabled &&
        (data.backgroundImage.url || data.backgroundImage.cloudfrontUrl) &&
        (() => {
          const bgImageUrl = data.backgroundImage.cloudfrontUrl || data.backgroundImage.url || '';
          const bgCrop = (data.backgroundImage as any).crop;
          const hasEffects = hasCropApplied(bgCrop);
          const cropStyles = getCroppedImageStyles(bgCrop);
          const rawSize = data.backgroundImage.size || 'cover';
          const baseImgStyles: React.CSSProperties = {
            objectFit: rawSize === 'contain' ? 'contain' : 'cover',
            objectPosition: getBackgroundPosition(data.backgroundImage.position),
          };
          const altText = data.backgroundImage.alt || 'CTA background';

          return (
            <div
              className={`absolute inset-0 overflow-hidden ${
                data.backgroundImage.parallax ? 'parallax-bg' : ''
              }`}
            >
              <img
                src={bgImageUrl}
                alt={altText}
                data-seo-image-role="content"
                className="absolute inset-0 h-full w-full"
                style={hasEffects ? { ...baseImgStyles, ...cropStyles } : baseImgStyles}
              />
            </div>
          );
        })()}

      {/* Background Image Overlay */}
      {data.backgroundImage?.enabled && data.backgroundImage.overlay?.enabled && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: data.backgroundImage.overlay.color || '#000000',
            opacity: data.backgroundImage.overlay.opacity ?? 0.5,
          }}
        />
      )}

      <style>{`
        .cta-button-${component.id}:hover {
          opacity: 0.9;
        }
      `}</style>
      {/* Background Pattern for some themes */}
      {currentTheme === 'gradient' && (
        <>
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0 opacity-10">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
                  '<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#ffffff" fill-opacity="0.05"><circle cx="30" cy="30" r="2"/></g></g></svg>'
                )}")`,
                backgroundRepeat: 'repeat',
              }}
            ></div>
          </div>
        </>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`max-w-4xl ${
            data.alignment === 'center' ? 'mx-auto' : data.alignment === 'right' ? 'ml-auto' : ''
          }`}
        >
          {/* Title */}
          {data.title && (
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight ${
                alignmentClasses[data.alignment]
              }`}
              style={{ color: textColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.title),
              }}
            />
          )}

          {/* Subtitle */}
          {data.subtitle && (
            <p
              className={`text-lg md:text-xl mb-8 leading-relaxed max-w-3xl ${
                alignmentClasses[data.alignment]
              } ${data.alignment === 'center' ? 'mx-auto' : data.alignment === 'right' ? 'ml-auto' : ''} opacity-90`}
              style={{ color: textColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.subtitle),
              }}
            />
          )}

          {/* Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 ${
              data.alignment === 'center'
                ? 'justify-center'
                : data.alignment === 'right'
                  ? 'justify-end'
                  : 'justify-start'
            }`}
          >
            {(data.buttons || []).map((button, index) => (
              <ButtonLink
                key={index}
                url={button.url}
                target={(button as any).target}
                className={`cta-button-${component.id} px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block text-center`}
                style={getButtonStyles(button.style)}
              >
                {button.text}
              </ButtonLink>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      {(currentTheme === 'gradient' || currentTheme === 'playful') && (
        <>
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-white/5 rounded-full blur-3xl"></div>
        </>
      )}
    </section>
  );
};
